/**
 * Panel de notas de voz: grabación con vúmetro, pausa/continuar, lista + scrubber.
 * Layouts: rail (Studio), dock-right (Classic), dock-bottom (Edge).
 */
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import {
  mdiClose,
  mdiMicrophone,
  mdiPause,
  mdiPlay,
  mdiStop,
  mdiDelete,
  mdiShareVariant,
  mdiDownload,
  mdiContentSave,
  mdiTools,
} from '@mdi/js'
import VoiceMeter from './VoiceMeter'
import { formatBytes } from '../utils/sharePayload'
import {
  getSttPrefsSnapshot,
  setFinalModelChoice,
  setLiveModelChoice,
  setWebSpeechPref,
  setShowTimestampsPref,
  setTranscribeDesktopPref,
  setTranscribeMobilePref,
  WHISPER_MODEL_META,
} from '../voice/sttPrefs'
import { formatWhisperModelShort } from '../voice/whisperModelCache'
import '../skins/voice.css'

function formatDuration(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r < 10 ? '0' : ''}${r}`
}

function formatSec(sec) {
  return formatDuration((Number(sec) || 0) * 1000)
}

/**
 * @param {object} props
 */
function VoiceNotesPanel({
  open,
  skin = 'classic',
  layout = 'dock-right',
  recording = false,
  paused = false,
  busy = false,
  canPause = true,
  stream = null,
  elapsedMs = 0,
  liveTranscript = null,
  notes = [],
  playingId = null,
  playProgress = null,
  onClose,
  onStart,
  onPause,
  onResume,
  onStop,
  onCancel,
  onPlay,
  onStopPlay,
  onSeek,
  onShare,
  onDownload,
  onDelete,
  onRetranscribe,
  modelProgress = null,
  sttRuntime = null,
  canLocalWhisper = false,
  transcribeOnDevice = true,
  onSttPrefsChange,
}) {
  const [tick, setTick] = useState(0)
  const [sttOpen, setSttOpen] = useState(false)
  const [prefs, setPrefs] = useState(() => getSttPrefsSnapshot())
  const [retranscribeTier, setRetranscribeTier] = useState({})
  const compact = layout === 'dock-bottom'

  const refreshPrefs = () => {
    try {
      setPrefs(getSttPrefsSnapshot())
    } catch (e) {
      /* ignore */
    }
  }

  useEffect(() => {
    if (open) refreshPrefs()
  }, [open])

  useEffect(() => {
    if (!open || !recording || paused) return undefined
    const id = setInterval(() => setTick((n) => n + 1), 250)
    return () => clearInterval(id)
  }, [open, recording, paused])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const root = document.documentElement
    if (open && layout === 'dock-bottom') {
      root.classList.add('udv-voice-dock-bottom-open')
    } else {
      root.classList.remove('udv-voice-dock-bottom-open')
    }
    if (open && layout === 'dock-right') {
      root.classList.add('udv-voice-dock-right-open')
    } else {
      root.classList.remove('udv-voice-dock-right-open')
    }
    return () => {
      root.classList.remove('udv-voice-dock-bottom-open')
      root.classList.remove('udv-voice-dock-right-open')
    }
  }, [open, layout])

  if (!open) return null

  const displayMs = recording ? elapsedMs : 0
  void tick

  const runtime = sttRuntime || prefs.sttRuntime || null
  const backendKey = (runtime && runtime.backend) || 'wasm'
  const backendLabel =
    (runtime && runtime.backendLabel) ||
    (backendKey === 'wasm' ? 'WASM' : String(backendKey).toUpperCase())
  const modelsWarm =
    runtime && Array.isArray(runtime.modelsWarm) ? runtime.modelsWarm : []
  const warmShort = modelsWarm.map(formatWhisperModelShort).filter(Boolean)
  const runtimeReady = runtime && runtime.status === 'ready' && warmShort.length > 0
  const loadSourceLabel =
    runtime && runtime.lastLoadSource === 'network'
      ? 'red'
      : runtime && runtime.lastLoadSource === 'memory'
        ? 'memoria'
        : runtime && runtime.lastLoadSource === 'cache'
          ? 'caché'
          : null

  const statusLabel = busy
    ? 'Procesando…'
    : recording
      ? paused
        ? 'En pausa'
        : 'Grabando'
      : 'Listo'

  const panel = (
    <div
      className={`udv-voice-panel layout-${layout} skin-${skin}`}
      role="complementary"
      aria-label="Notas de voz"
    >
      <header className="udv-voice-header">
        <div className="udv-voice-title">
          <Icon path={mdiMicrophone} size={0.85} color="currentColor" />
          <span>Notas de voz</span>
          <span className={`udv-voice-badge${recording && !paused ? ' is-live' : ''}`}>
            {statusLabel}
          </span>
          {canLocalWhisper ? (
            <span className="udv-voice-stt-badges" aria-label="Runtime STT">
              <span
                className={`udv-voice-stt-badge udv-voice-stt-badge--wasm${
                  runtimeReady ? ' is-warm' : ''
                }`}
                title={`Backend: ${backendLabel}${
                  warmShort.length ? ` · modelos: ${warmShort.join(', ')}` : ''
                }`}
              >
                {backendLabel}
              </span>
              {runtime && runtime.npuAvailable ? (
                <span
                  className="udv-voice-stt-badge udv-voice-stt-badge--npu is-disabled"
                  title="NPU detectada — experimento próximamente"
                >
                  NPU
                </span>
              ) : null}
              {runtime && runtime.npuActive ? (
                <span
                  className="udv-voice-stt-badge udv-voice-stt-badge--npu is-active"
                  title="Transcripción en NPU"
                >
                  NPU activa
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="udv-voice-icon-btn"
          onClick={onClose}
          aria-label={layout === 'rail' ? 'Volver a herramientas' : 'Cerrar panel'}
          title={
            layout === 'rail'
              ? 'Volver a herramientas (la grabación sigue en el HUD)'
              : recording
                ? 'Cerrar panel (seguís con el HUD en el viewport)'
                : 'Cerrar'
          }
        >
          <Icon
            path={layout === 'rail' ? mdiTools : mdiClose}
            size={0.9}
            color="currentColor"
          />
        </button>
      </header>

      <section className="udv-voice-rec">
        <VoiceMeter
          stream={stream}
          active={!!recording && !!stream}
          paused={!!paused}
          height={compact ? 48 : 72}
          variant="full"
        />
        <div className="udv-voice-timer-row">
          <div className="udv-voice-timer" aria-live="polite">
            {formatDuration(displayMs)}
          </div>
          <div className="udv-voice-controls">
            {!recording ? (
              <button
                type="button"
                className="udv-voice-btn primary"
                disabled={busy}
                onClick={onStart}
              >
                <Icon path={mdiMicrophone} size={0.8} color="currentColor" />
                Grabar
              </button>
            ) : (
              <>
                {canPause ? (
                  paused ? (
                    <button
                      type="button"
                      className="udv-voice-btn"
                      disabled={busy}
                      onClick={onResume}
                    >
                      <Icon path={mdiPlay} size={0.8} color="currentColor" />
                      Continuar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="udv-voice-btn"
                      disabled={busy}
                      onClick={onPause}
                    >
                      <Icon path={mdiPause} size={0.8} color="currentColor" />
                      Pausar
                    </button>
                  )
                ) : null}
                <button
                  type="button"
                  className="udv-voice-btn danger"
                  disabled={busy}
                  onClick={onStop}
                >
                  <Icon path={mdiStop} size={0.8} color="currentColor" />
                  Fin
                </button>
                <button
                  type="button"
                  className="udv-voice-btn ghost"
                  disabled={busy}
                  onClick={onCancel}
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>
        {busy ? (
          <p className="udv-voice-hint">
            <Icon path={mdiContentSave} size={0.65} color="currentColor" /> MP3…
          </p>
        ) : (
          <p className="udv-voice-hint">
            {recording
              ? 'Podés cerrar este panel: el HUD del viewport sigue activo.'
              : 'Revisá con el slider · compartí desde la lista.'}
          </p>
        )}
        {recording ? (
          <p
            className={`udv-voice-live-draft vad-${
              (liveTranscript && liveTranscript.vadState) ||
              (liveTranscript && liveTranscript.status) ||
              'listening'
            }${
              liveTranscript && liveTranscript.status === 'error'
                ? ' is-error'
                : ''
            }`}
            aria-live="polite"
          >
            {liveTranscript && liveTranscript.status === 'error' ? (
              <span className="udv-voice-live-interim">
                {liveTranscript.error || 'Dictado en vivo no disponible'}
              </span>
            ) : liveTranscript &&
              (liveTranscript.finalText || liveTranscript.interimText) ? (
              <>
                <span className="udv-voice-live-final">
                  {liveTranscript.finalText}
                </span>
                {liveTranscript.interimText ? (
                  <span className="udv-voice-live-interim">
                    {' '}
                    {liveTranscript.interimText}
                  </span>
                ) : null}
              </>
            ) : liveTranscript && liveTranscript.text ? (
              <span className="udv-voice-live-final">{liveTranscript.text}</span>
            ) : (
              <span className="udv-voice-live-interim">
                Escuchando… (Whisper local · todos los browsers)
              </span>
            )}
          </p>
        ) : null}
      </section>

      <section className="udv-voice-stt-settings">
        <button
          type="button"
          className="udv-voice-stt-toggle"
          onClick={() => setSttOpen((v) => !v)}
        >
          Ajustes STT / Whisper {sttOpen ? '▾' : '▸'}
        </button>
        {modelProgress && modelProgress.message ? (
          <p className="udv-voice-model-progress" aria-live="polite">
            {modelProgress.message}
            {modelProgress.percent != null
              ? ` ${Math.round(modelProgress.percent)}%`
              : ''}
          </p>
        ) : null}
        {sttOpen ? (
          <div className="udv-voice-stt-body">
            <fieldset className="udv-voice-stt-transcribe-prefs">
              <legend>Transcripción (la grabación MP3 siempre queda guardada)</legend>
              <label className="udv-voice-stt-check">
                <input
                  type="checkbox"
                  checked={!!prefs.transcribeDesktop}
                  disabled={!!recording || !!busy}
                  onChange={(e) => {
                    setTranscribeDesktopPref(!!e.target.checked)
                    refreshPrefs()
                    if (onSttPrefsChange) onSttPrefsChange()
                  }}
                />
                Transcribir en desktop (Whisper local)
              </label>
              <label className="udv-voice-stt-check">
                <input
                  type="checkbox"
                  checked={!!prefs.transcribeMobile}
                  disabled={!!recording || !!busy}
                  onChange={(e) => {
                    setTranscribeMobilePref(!!e.target.checked)
                    refreshPrefs()
                  }}
                />
                Transcribir en móvil (dictado en vivo / futuro Gateway)
              </label>
              {!transcribeOnDevice ? (
                <p className="udv-voice-stt-hint muted">
                  En este dispositivo la transcripción está desactivada; solo se
                  guardará el audio.
                </p>
              ) : null}
            </fieldset>
            <p className="udv-voice-stt-hint">
              Sugerido en este equipo — live:{' '}
              <strong>{prefs.liveSuggested}</strong> ({prefs.liveReason}). Final:{' '}
              <strong>{prefs.finalSuggested}</strong> ({prefs.finalReason}). Backend:{' '}
              {prefs.backend}.
            </p>
            <label className="udv-voice-stt-row">
              <span>Modelo live</span>
              <select
                value={prefs.liveChoice || 'auto'}
                disabled={!!recording || !!busy}
                onChange={(e) => {
                  setLiveModelChoice(e.target.value)
                  refreshPrefs()
                  if (onSttPrefsChange) onSttPrefsChange()
                }}
              >
                <option value="auto">
                  Auto → {prefs.liveSuggested} (recomendado live)
                </option>
                <option value="tiny">
                  Tiny {WHISPER_MODEL_META.tiny.sizeHint} — {WHISPER_MODEL_META.tiny.liveHint}
                </option>
                <option value="base">
                  Base {WHISPER_MODEL_META.base.sizeHint} — {WHISPER_MODEL_META.base.liveHint}
                </option>
                <option value="small">
                  Small {WHISPER_MODEL_META.small.sizeHint} — {WHISPER_MODEL_META.small.liveHint}
                </option>
              </select>
            </label>
            <label className="udv-voice-stt-row">
              <span>Modelo al finalizar / re-transcribir</span>
              <select
                value={prefs.finalChoice || 'auto'}
                disabled={!!recording || !!busy}
                onChange={(e) => {
                  setFinalModelChoice(e.target.value)
                  refreshPrefs()
                  if (onSttPrefsChange) onSttPrefsChange()
                }}
              >
                <option value="auto">
                  Auto → {prefs.finalSuggested} (recomendado final)
                </option>
                <option value="tiny">
                  Tiny — {WHISPER_MODEL_META.tiny.finalHint}
                </option>
                <option value="base">
                  Base — {WHISPER_MODEL_META.base.finalHint}
                </option>
                <option value="small">
                  Small — {WHISPER_MODEL_META.small.finalHint}
                </option>
              </select>
            </label>
            {prefs.chromeSpeechUi ? (
              <label className="udv-voice-stt-check">
                <input
                  type="checkbox"
                  checked={!!prefs.webSpeech}
                  disabled={!!recording}
                  onChange={(e) => {
                    setWebSpeechPref(!!e.target.checked)
                    refreshPrefs()
                  }}
                />
                Usar Web Speech (Google) en vivo — Chrome/Edge
              </label>
            ) : (
              <p className="udv-voice-stt-hint muted">
                Web Speech (Google) no se ofrece aquí (Brave/Firefox u otro). Live =
                Whisper local.
              </p>
            )}
            <label className="udv-voice-stt-check">
              <input
                type="checkbox"
                checked={!!prefs.showTimestamps}
                onChange={(e) => {
                  setShowTimestampsPref(!!e.target.checked)
                  refreshPrefs()
                }}
              />
              Mostrar timestamps por palabra (si hay)
            </label>
            <label className="udv-voice-stt-check is-disabled">
              <input type="checkbox" disabled checked={false} readOnly />
              NPU / WebNN (próximamente)
              {runtime && runtime.npuAvailable
                ? ' — hardware compatible detectado'
                : ''}
            </label>
            <p className="udv-voice-stt-hint muted">
              Activo ahora: live <code>{prefs.liveActive}</code> · final{' '}
              <code>{prefs.finalActive}</code>
              {prefs.hasWebGpu ? ' · GPU detectada' : ' · sin WebGPU'} ·{' '}
              {prefs.hardwareConcurrency || '?'} cores
              {prefs.deviceMemory != null ? ` · ${prefs.deviceMemory} GiB` : ''}
            </p>
            {canLocalWhisper ? (
              <p className="udv-voice-stt-hint muted">
                Runtime: <strong>{backendLabel}</strong>
                {warmShort.length
                  ? ` · modelos en memoria: ${warmShort.join(', ')}`
                  : ' · sin modelos precargados'}
                {loadSourceLabel ? ` · última carga: ${loadSourceLabel}` : ''}
                {runtime && runtime.status === 'inferring' ? ' · transcribiendo…' : ''}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="udv-voice-list-wrap">
        <h3 className="udv-voice-list-title">
          Guardadas {notes.length ? `(${notes.length})` : ''}
        </h3>
        {!notes.length ? (
          <p className="udv-voice-empty">Sin notas en esta sesión.</p>
        ) : (
          <ul className="udv-voice-list">
            {notes.map((n) => {
              const isPlaying = playingId === n.id
              const prog =
                playProgress && playProgress.id === n.id ? playProgress : null
              const durationSec =
                (prog && prog.duration) ||
                (n.durationMs ? n.durationMs / 1000 : 0) ||
                0
              const currentSec = prog ? prog.current : 0
              return (
                <li key={n.id} className={`udv-voice-item${isPlaying ? ' is-playing' : ''}`}>
                  <div className="udv-voice-item-main">
                    <div className="udv-voice-item-meta">
                      <strong>{n.name}</strong>
                      <span>
                        {formatDuration(n.durationMs)} · {formatBytes(n.size)}
                      </span>
                    </div>
                    <div className="udv-voice-item-actions">
                      <button
                        type="button"
                        className="udv-voice-icon-btn"
                        title={isPlaying ? 'Detener' : 'Reproducir'}
                        onClick={() => (isPlaying ? onStopPlay() : onPlay(n.id))}
                      >
                        <Icon
                          path={isPlaying ? mdiStop : mdiPlay}
                          size={0.75}
                          color="currentColor"
                        />
                      </button>
                      <button
                        type="button"
                        className="udv-voice-icon-btn"
                        title="Compartir"
                        onClick={() => onShare(n.id)}
                      >
                        <Icon path={mdiShareVariant} size={0.75} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        className="udv-voice-icon-btn"
                        title="Descargar"
                        onClick={() => onDownload(n.id)}
                      >
                        <Icon path={mdiDownload} size={0.75} color="currentColor" />
                      </button>
                      <button
                        type="button"
                        className="udv-voice-icon-btn danger"
                        title="Eliminar"
                        onClick={() => onDelete(n.id)}
                      >
                        <Icon path={mdiDelete} size={0.75} color="currentColor" />
                      </button>
                    </div>
                  </div>
                  {(isPlaying || prog) && durationSec > 0 ? (
                    <div className="udv-voice-scrub">
                      <span className="udv-voice-scrub-t">{formatSec(currentSec)}</span>
                      <input
                        type="range"
                        className="udv-voice-scrub-range"
                        min={0}
                        max={Math.max(0.1, durationSec)}
                        step={0.05}
                        value={Math.min(currentSec, durationSec)}
                        aria-label={`Posición en ${n.name}`}
                        onChange={(e) => {
                          const t = Number(e.target.value)
                          if (onSeek) onSeek(n.id, t)
                        }}
                      />
                      <span className="udv-voice-scrub-t">{formatSec(durationSec)}</span>
                    </div>
                  ) : null}
                  {n.transcriptStatus === 'pending' ? (
                    <p className="udv-voice-transcript is-pending">
                      {n.liveDraft
                        ? `Refinando… ${n.liveDraft}`
                        : 'Transcribiendo… (primera vez puede tardar: descarga del modelo)'}
                    </p>
                  ) : n.transcriptStatus === 'error' ? (
                    <p className="udv-voice-transcript is-error">
                      No se pudo transcribir (el audio quedó guardado).
                    </p>
                  ) : n.transcript && n.transcript.text ? (
                    <div className="udv-voice-transcript-block">
                      <p
                        className={`udv-voice-transcript${
                          n.transcript.source === 'hybrid' ? ' is-hybrid' : ''
                        }`}
                        title={n.transcript.text}
                      >
                        {n.transcript.source === 'hybrid' ? (
                          <span className="udv-voice-transcript-tag">hybrid</span>
                        ) : null}
                        {n.transcript.modelTier || n.transcript.modelId ? (
                          <span className="udv-voice-transcript-tag model">
                            {n.transcript.modelTier ||
                              String(n.transcript.modelId).replace(/^.*whisper-/, '')}
                          </span>
                        ) : null}
                        {n.transcript.text}
                      </p>
                      {prefs.showTimestamps &&
                      Array.isArray(n.transcript.words) &&
                      n.transcript.words.length ? (
                        <p className="udv-voice-timestamps">
                          {n.transcript.words
                            .map((w) => {
                              const word = w.word || w.text || ''
                              const a =
                                w.start != null ? Number(w.start).toFixed(1) : '?'
                              const b =
                                w.end != null ? Number(w.end).toFixed(1) : '?'
                              return `${word}(${a}–${b}s)`
                            })
                            .join(' ')}
                        </p>
                      ) : null}
                    </div>
                  ) : n.transcriptStatus === 'skipped' ? (
                    <p className="udv-voice-transcript is-skipped">
                      {transcribeOnDevice
                        ? 'Sin transcripción local en este dispositivo'
                        : 'Transcripción desactivada (solo audio guardado)'}
                    </p>
                  ) : null}
                  {canLocalWhisper && n.mp3Blob && n.transcriptStatus !== 'pending' ? (
                    <div className="udv-voice-retranscribe">
                      <select
                        value={
                          retranscribeTier[n.id] ||
                          prefs.finalSuggested ||
                          'base'
                        }
                        disabled={!!busy || !!recording}
                        onChange={(e) =>
                          setRetranscribeTier((prev) => ({
                            ...prev,
                            [n.id]: e.target.value,
                          }))
                        }
                        aria-label="Modelo para re-transcribir"
                      >
                        <option value="tiny">Re-transcribir · tiny</option>
                        <option value="base">Re-transcribir · base</option>
                        <option value="small">Re-transcribir · small</option>
                      </select>
                      <button
                        type="button"
                        className="udv-voice-btn ghost compact"
                        disabled={!!busy || !!recording || !onRetranscribe}
                        onClick={() => {
                          const tier =
                            retranscribeTier[n.id] ||
                            prefs.finalSuggested ||
                            'base'
                          if (onRetranscribe) onRetranscribe(n.id, tier)
                        }}
                      >
                        Re-transcribir
                      </button>
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )

  if (layout === 'rail') {
    return panel
  }

  return createPortal(
    <div className={`udv-voice-sheet layout-${layout} skin-${skin}`} aria-hidden={false}>
      {panel}
    </div>,
    document.body
  )
}

export default VoiceNotesPanel
